import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  Grid,
  Card,
  CardContent,
  TextField,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  useMediaQuery,
  Snackbar,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Assignment as AssignmentIcon,
  Inventory as InventoryIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  getPurchaseOrderForReceiving, 
  submitPurchaseOrderReceiving, 
  markPurchaseOrderReceived 
} from '../../collections/purchaseOrders';

const StaffPOReceiving = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [purchaseOrder, setPurchaseOrder] = useState(null);
  const [receivingQuantities, setReceivingQuantities] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [confirmDialog, setConfirmDialog] = useState({ open: false, action: null });
  const [expandedRows, setExpandedRows] = useState(new Set());
  
  const brandColors = {
    primary: '#0e2a04',
    secondary: '#6b8e4e',
    accent: '#d4a574',
    light: '#c8d6b8',
    background: '#f5f8f3',
    success: '#2e7d32',
    warning: '#f57c00',
    error: '#d32f2f'
  };

  // Load PO receiving data
  useEffect(() => {
    const loadReceivingData = async () => {
      try {
        const response = await getPurchaseOrderForReceiving(id);
        
        setPurchaseOrder(response.data);
        
        // Initialize receiving quantities to 0
        const initialQuantities = {};
        response.data.items.forEach(item => {
          initialQuantities[item.product_id] = 0;
        });
        setReceivingQuantities(initialQuantities);
        
      } catch (error) {
        console.error('Failed to load PO receiving data:', error);
        setError('Không thể tải thông tin đơn hàng. Vui lòng thử lại.');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      loadReceivingData();
    }
  }, [id]);

  const handleQuantityChange = useCallback((productId, value) => {
    const numValue = parseFloat(value) || 0;
    setReceivingQuantities(prev => ({
      ...prev,
      [productId]: numValue >= 0 ? numValue : 0
    }));
  }, []);

  const handleQuickQuantity = useCallback((productId, action) => {
    setReceivingQuantities(prev => {
      const currentValue = prev[productId] || 0;
      let newValue = currentValue;
      
      switch (action) {
        case 'add0.5':
          newValue = currentValue + 0.5;
          break;
        case 'add1':
          newValue = currentValue + 1;
          break;
        case 'add5':
          newValue = currentValue + 5;
          break;
        case 'clear':
          newValue = 0;
          break;
        default:
          break;
      }
      
      return {
        ...prev,
        [productId]: newValue >= 0 ? newValue : 0
      };
    });
  }, []);

  const toggleRowExpansion = useCallback((productId) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(productId)) {
        newSet.delete(productId);
      } else {
        newSet.add(productId);
      }
      return newSet;
    });
  }, []);

  const handleSubmitReceiving = async () => {
    const itemsToReceive = Object.entries(receivingQuantities)
      .filter(([productId, quantity]) => quantity > 0)
      .map(([productId, quantity]) => ({
        product_id: parseInt(productId),
        quantity_received: quantity,
        notes: ''
      }));

    if (itemsToReceive.length === 0) {
      setError('Vui lòng nhập số lượng nhận cho ít nhất một sản phẩm.');
      return;
    }

    setSubmitting(true);
    try {
      const response = await submitPurchaseOrderReceiving(id, itemsToReceive);
      
      setSuccessMessage(`Đã nhận ${itemsToReceive.length} sản phẩm thành công!`);
      
      // Reset receiving quantities and reload data
      const resetQuantities = {};
      Object.keys(receivingQuantities).forEach(productId => {
        resetQuantities[productId] = 0;
      });
      setReceivingQuantities(resetQuantities);
      
      // Reload PO data to show updated totals
      window.location.reload();
      
    } catch (error) {
      console.error('Failed to submit receiving:', error);
      setError(error.response?.data?.error || 'Không thể ghi nhận việc nhận hàng. Vui lòng thử lại.');
    } finally {
      setSubmitting(false);
      setConfirmDialog({ open: false, action: null });
    }
  };

  const handleMarkFullyReceived = async () => {
    setSubmitting(true);
    try {
      await markPurchaseOrderReceived(id);
      
      setSuccessMessage('Đã đánh dấu đơn hàng là hoàn thành!');
      setTimeout(() => {
        navigate('/purchase-orders');
      }, 2000);
      
    } catch (error) {
      console.error('Failed to mark as received:', error);
      setError(error.response?.data?.error || 'Không thể đánh dấu hoàn thành. Vui lòng thử lại.');
    } finally {
      setSubmitting(false);
      setConfirmDialog({ open: false, action: null });
    }
  };

  const getVarianceColor = (variance) => {
    if (variance === 0) return brandColors.success;
    if (variance > 0) return brandColors.warning;
    return brandColors.error;
  };

  const getVarianceText = (variance) => {
    if (variance === 0) return 'Đúng';
    if (variance > 0) return `+${variance}`;
    return `${variance}`;
  };

  if (loading) {
    return (
      <Container>
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Typography>Đang tải...</Typography>
        </Box>
      </Container>
    );
  }

  if (!purchaseOrder) {
    return (
      <Container>
        <Alert severity="error" sx={{ mt: 2 }}>
          Không tìm thấy đơn đặt hàng
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Typography 
        variant="h4" 
        sx={{ 
          mb: 3,
          color: brandColors.primary,
          fontWeight: 700,
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }}
      >
        <AssignmentIcon fontSize="large" />
        Nhận hàng - {purchaseOrder.po_number}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Historical PO Warning */}
      {purchaseOrder.is_historical && (
        <Alert
          severity="warning"
          sx={{
            mb: 3,
            borderRadius: '12px',
            backgroundColor: 'rgba(158, 158, 158, 0.15)',
            '& .MuiAlert-icon': {
              color: '#9e9e9e'
            }
          }}
        >
          <Typography sx={{ fontWeight: 600, color: '#5d4037' }}>
            Đây là đơn hàng lịch sử
          </Typography>
          <Typography variant="body2" sx={{ color: '#5d4037' }}>
            Việc nhận hàng sẽ KHÔNG ảnh hưởng đến số lượng tồn kho hiện tại. Đơn hàng này chỉ để ghi nhận lịch sử.
          </Typography>
        </Alert>
      )}

      {/* PO Information Card */}
      <Card sx={{ mb: 3, borderRadius: '16px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" sx={{ color: brandColors.primary, mb: 1, fontWeight: 600 }}>
                Thông tin đơn hàng
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography><strong>Số PO:</strong> {purchaseOrder.po_number}</Typography>
                {purchaseOrder.is_historical && (
                  <Chip
                    label="Lịch sử"
                    size="small"
                    sx={{
                      backgroundColor: '#9e9e9e',
                      color: 'white',
                      fontSize: '0.7rem',
                      height: '20px',
                      fontWeight: 500,
                    }}
                  />
                )}
              </Box>
              {purchaseOrder.supplier_name && (
                <Typography><strong>Nhà cung cấp:</strong> {purchaseOrder.supplier_name}</Typography>
              )}
              <Typography><strong>Ngày đặt:</strong> {purchaseOrder.order_date}</Typography>
              <Typography><strong>Trạng thái:</strong>
                <Chip
                  label={purchaseOrder.status === 'SENT' ? 'Đã gửi' :
                        purchaseOrder.status === 'PARTIALLY_RECEIVED' ? 'Đã nhận một phần' :
                        purchaseOrder.status}
                  color={purchaseOrder.status === 'SENT' ? 'primary' : 'warning'}
                  size="small"
                  sx={{ ml: 1 }}
                />
              </Typography>
            </Grid>
            {purchaseOrder.notes && (
              <Grid item xs={12} md={6}>
                <Typography variant="h6" sx={{ color: brandColors.primary, mb: 1, fontWeight: 600 }}>
                  Ghi chú
                </Typography>
                <Typography>{purchaseOrder.notes}</Typography>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      {/* Receiving Table */}
      <Card sx={{ borderRadius: '16px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
        <CardContent>
          <Typography 
            variant="h6" 
            sx={{ 
              mb: 3, 
              color: brandColors.primary, 
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}
          >
            <InventoryIcon />
            Chi tiết nhận hàng
          </Typography>

          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size={isMobile ? 'small' : 'medium'}>
              <TableHead>
                <TableRow sx={{ backgroundColor: brandColors.background }}>
                  <TableCell sx={{ fontWeight: 600, color: brandColors.primary }}>
                    Sản phẩm
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: brandColors.primary }} align="center">
                    Đã đặt
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: brandColors.primary }} align="center">
                    Đã nhận
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: brandColors.primary }} align="center">
                    Chênh lệch
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: brandColors.primary }} align="center">
                    Nhận hôm nay
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: brandColors.primary }} align="center">
                    Thao tác
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: brandColors.primary }} align="center">
                    Lịch sử
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {purchaseOrder.items.map((item) => (
                  <React.Fragment key={item.product_id}>
                  <TableRow hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {item.product_name}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2">
                        {item.quantity_ordered}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2">
                        {item.quantity_received_total}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={getVarianceText(item.variance)}
                        size="small"
                        sx={{ 
                          backgroundColor: getVarianceColor(item.variance),
                          color: 'white',
                          fontWeight: 500
                        }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <TextField
                        type="number"
                        size="small"
                        value={receivingQuantities[item.product_id] || 0}
                        onChange={(e) => handleQuantityChange(item.product_id, e.target.value)}
                        inputProps={{ min: 0, step: 0.01 }}
                        sx={{ width: '80px' }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', justifyContent: 'center' }}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleQuickQuantity(item.product_id, 'add0.5')}
                          sx={{ minWidth: '32px', padding: '4px', fontSize: '0.7rem' }}
                        >
                          +0.5
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleQuickQuantity(item.product_id, 'add1')}
                          sx={{ minWidth: '32px', padding: '4px' }}
                        >
                          +1
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleQuickQuantity(item.product_id, 'add5')}
                          sx={{ minWidth: '32px', padding: '4px' }}
                        >
                          +5
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleQuickQuantity(item.product_id, 'clear')}
                          color="error"
                          sx={{ minWidth: '32px', padding: '4px' }}
                        >
                          0
                        </Button>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Button
                        size="small"
                        onClick={() => toggleRowExpansion(item.product_id)}
                        startIcon={expandedRows.has(item.product_id) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        sx={{ minWidth: '32px', padding: '4px' }}
                      >
                        {item.receiving_history?.length || 0}
                      </Button>
                    </TableCell>
                  </TableRow>
                  
                  {/* Expanded History Row */}
                  {expandedRows.has(item.product_id) && (
                    <TableRow>
                      <TableCell colSpan={7} sx={{ backgroundColor: brandColors.background, py: 2 }}>
                        <Box sx={{ ml: 2 }}>
                          <Typography variant="subtitle2" sx={{ 
                            fontWeight: 600, 
                            color: brandColors.primary, 
                            mb: 1,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1
                          }}>
                            <HistoryIcon fontSize="small" />
                            Lịch sử nhận hàng - {item.product_name}
                          </Typography>
                          {item.receiving_history && item.receiving_history.length > 0 ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                              {item.receiving_history.map((history, index) => (
                                <Box key={index} sx={{ 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  gap: 2,
                                  p: 1,
                                  backgroundColor: 'white',
                                  borderRadius: '8px',
                                  border: `1px solid ${brandColors.light}`
                                }}>
                                  <Typography variant="body2" sx={{ 
                                    minWidth: '80px',
                                    fontWeight: 500,
                                    color: brandColors.primary
                                  }}>
                                    {new Date(history.date).toLocaleDateString('vi-VN')}
                                  </Typography>
                                  <Chip 
                                    label={`${history.quantity} kg`}
                                    size="small"
                                    color="success"
                                    sx={{ minWidth: '60px' }}
                                  />
                                  <Typography variant="body2" color="textSecondary" sx={{ flex: 1 }}>
                                    {history.description || 'Không có ghi chú'}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          ) : (
                            <Typography variant="body2" color="textSecondary" sx={{ 
                              fontStyle: 'italic',
                              p: 2,
                              textAlign: 'center',
                              backgroundColor: 'white',
                              borderRadius: '8px',
                              border: `1px solid ${brandColors.light}`
                            }}>
                              Chưa có lịch sử nhận hàng
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => setConfirmDialog({ open: true, action: 'receive' })}
              disabled={submitting || Object.values(receivingQuantities).every(q => q === 0)}
              sx={{
                borderRadius: '12px',
                px: 4,
                py: 1.5,
                fontWeight: 600
              }}
            >
              {submitting ? 'Đang xử lý...' : 'Xác nhận nhận hàng'}
            </Button>
            
            <Button
              variant="outlined"
              color="success"
              onClick={() => setConfirmDialog({ open: true, action: 'markReceived' })}
              disabled={submitting}
              startIcon={<CheckIcon />}
              sx={{
                borderRadius: '12px',
                px: 4,
                py: 1.5,
                fontWeight: 600
              }}
            >
              Đánh dấu hoàn thành
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog({ open: false, action: null })}>
        <DialogTitle>
          {confirmDialog.action === 'receive' ? 'Xác nhận nhận hàng' : 'Đánh dấu hoàn thành'}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {confirmDialog.action === 'receive' 
              ? 'Bạn có chắc chắn muốn ghi nhận việc nhận hàng với số lượng đã nhập?'
              : 'Bạn có chắc chắn muốn đánh dấu đơn hàng này là đã hoàn thành việc nhận hàng?'
            }
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setConfirmDialog({ open: false, action: null })}
            disabled={submitting}
          >
            Hủy
          </Button>
          <Button 
            onClick={confirmDialog.action === 'receive' ? handleSubmitReceiving : handleMarkFullyReceived}
            variant="contained"
            disabled={submitting}
            color={confirmDialog.action === 'receive' ? 'primary' : 'success'}
          >
            {submitting ? 'Đang xử lý...' : 'Xác nhận'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={4000}
        onClose={() => setSuccessMessage('')}
      >
        <Alert severity="success" onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default StaffPOReceiving;